import { useEffect } from 'react'

import { useQuery } from '@tanstack/react-query'

import { apiGetBoundReferralCodeForWallet } from '@src/api/point.api'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { ReferralCode } from '@src/types/referralcode'
import { Address } from '@src/types/token'
import { createContextLogger, isEVMAddress, isFeatureEnabled } from '@src/utils'
import { useApplicationStore } from '@src/zustand_stores'

const logger = createContextLogger('useBoundReferralCode')

function useBoundReferralCodeQuery() {
    const { evmWalletAddress } = useWalletConnections()
    const boundReferralCode = useApplicationStore(state => state.boundReferralCode)

    return useQuery<ReferralCode, Error>({
        queryKey: ['boundReferralCode', evmWalletAddress],
        queryFn: async () => {
            // If we're on testnet, we don't support referral codes
            const isTestnet = isFeatureEnabled('USE_TESTNETS_ONLY')
            if (isTestnet) {
                return {
                    userAddress: '' as Address,
                    code: '',
                }
            }

            if (!evmWalletAddress || !isEVMAddress(evmWalletAddress)) {
                throw new Error('Invalid or missing EVM wallet address')
            }

            logger.info(`Fetching bound referral code for wallet ${evmWalletAddress}`)

            const data = await apiGetBoundReferralCodeForWallet(evmWalletAddress)
            if (!data) {
                throw new Error(`No bound referral code found for wallet ${evmWalletAddress}`)
            }

            return {
                userAddress: evmWalletAddress,
                code: data.toUpperCase(),
            }
        },

        // Only run if:
        // 1. There is a valid EVM wallet address
        // 2. AND either no referral code exists yet,
        //    or the existing code is for a different wallet.
        enabled:
            !!evmWalletAddress &&
            isEVMAddress(evmWalletAddress) &&
            (!boundReferralCode || boundReferralCode.userAddress.toLowerCase() !== evmWalletAddress.toLowerCase()),

        // Since referral codes likely don't change,
        // mark them as non-stale and disable auto-refetching.
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchInterval: false,
    })
}

/**
 * Hook for fetching the bound referral code.
 */
export function useFetchBoundReferralCode() {
    const { data, isLoading, error } = useBoundReferralCodeQuery()
    const setBoundReferralCode = useApplicationStore(state => state.setBoundReferralCode)

    useEffect(() => {
        logger.info('fetched bound referral code:', data)
        if (!isLoading) {
            if (data) {
                setBoundReferralCode(data)
            } else if (error) {
                // If wallet address is not connected or referral code
                // can't be fetched, set back to the default value.
                setBoundReferralCode({ userAddress: '' as Address, code: '' })
                logger.error(error)
            }
        }
    }, [data, isLoading, error])

    // We do not export referral code, instead we update the application store.
    return { isLoading, error }
}
